// Client-side error reporter (2026-09-22, Jaime directive).
// Errors that become visible on a user's screen must be registered server-side
// (POST /api/client-errors) so they land in Discord #bug-alerts via the
// error watchdog. Fire-and-forget: this module NEVER awaits, NEVER throws and
// NEVER shows UI — a failing endpoint must not break the app.
// Marker: CLIENT_ERROR

import { resolveApiBase } from './apiBase';

const API_URL = resolveApiBase();
// CONTEXT SNAPSHOT (2026-10-01, Jaime): api-failure payloads carry a capped
// tail of the selection trace so blank-screen reports correlate with unit
// selection history. Technical ids only — the sanitizer below caps size.
import { SEL_TRACE_KEY } from '../utils/selTrace';
import { SENSITIVE_KEY_RE, scrubText } from './piiScrub';

function selTraceTail(): unknown[] {
  try {
    const raw = sessionStorage.getItem(SEL_TRACE_KEY);
    const arr: unknown[] = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.slice(-20) : [];
  } catch { return []; }
}

const ENDPOINT = `${API_URL}/client-errors`;

const MAX_MESSAGE_LEN = 500;
const MAX_PER_SIGNATURE = 5; // throttle: max 5 reports per session per message-signature
const MAX_TOTAL = 40; // global session cap (defense against signature explosion)
const MAX_BODY_LEN = 4000; // hard cap on serialized context

// ---------------------------------------------------------------------------
// OFFLINE QUEUE (beacons add-ons Task 1, Jaime 2026-10-02 directive):
// fire-and-forget normally, but an enqueue failure (offline / network error)
// must not lose the report. Minimal localStorage queue:
//  - key vecindus_client_error_queue, hard cap 50 entries FIFO (drop-oldest)
//  - 24h expiry, single flush pass on app load + window 'online'
//  - flush re-checks the same dedupe caps, never requeues, never throws
// No IndexedDB, no retry backoff by design.
// ---------------------------------------------------------------------------
const QUEUE_KEY = 'vecindus_client_error_queue';
const QUEUE_MAX = 50;
const QUEUE_TTL_MS = 24 * 60 * 60 * 1000;

interface QueueEntry { t: number; body: string }

function readQueue(): QueueEntry[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    const arr: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(arr)) return [];
    const now = Date.now();
    return (arr as any[])
      .filter((e) => !!e && typeof e === 'object' && typeof e.body === 'string')
      .map((e) => ({ t: Number(e.t) || 0, body: String(e.body) }))
      .filter((e) => now - e.t <= QUEUE_TTL_MS);
  } catch { return []; }
}

function writeQueue(entries: QueueEntry[]): void {
  try {
    // FIFO hard cap: keep the newest QUEUE_MAX, drop-oldest beyond that.
    localStorage.setItem(QUEUE_KEY, JSON.stringify(entries.slice(-QUEUE_MAX)));
  } catch { /* storage unavailable/full: drop silently, never throw */ }
}

function enqueueOffline(body: string): void {
  try {
    const q = readQueue();
    q.push({ t: Date.now(), body });
    writeQueue(q); // cap enforced here (drop-oldest on overflow)
  } catch { /* never throw */ }
}

/** Fire-and-forget POST of one serialized body; queues on network failure. */
function deliver(body: string): void {
  try {
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {
      // Offline / network error: stash for the next flush (never rethrow).
      enqueueOffline(body);
    });
  } catch {
    try { enqueueOffline(body); } catch { /* ignore */ }
  }
}

/** Single flush pass: drop expired, respect dedupe caps, never requeue. */
function flushQueue(): void {
  try {
    const q = readQueue();
    if (!q.length) return;
    for (const entry of q) {
      if (totalSent >= MAX_TOTAL) break; // session cap: drop the rest silently
      let sig: string | null = null;
      try {
        const parsed = JSON.parse(entry.body);
        sig = `${parsed?.level || 'error'}::${String(parsed?.message || '').slice(0, MAX_MESSAGE_LEN)}`;
      } catch { sig = null; } // unparseable legacy entry: still delivered once
      if (sig) {
        const c = sentCounts.get(sig) || 0;
        if (c >= MAX_PER_SIGNATURE) continue; // cap reached: drop, don't send
        sentCounts.set(sig, c + 1);
      }
      totalSent++;
      deliver(entry.body);
    }
    // Single pass: the queue is drained regardless of send outcome (no retry).
    try { localStorage.removeItem(QUEUE_KEY); } catch { /* ignore */ }
  } catch { /* never throw */ }
}

if (typeof window !== 'undefined') {
  try {
    window.addEventListener('online', () => { try { flushQueue(); } catch { /* ignore */ } });
    // Flush offline-queued beacons from a previous session once app load settles.
    setTimeout(() => { try { flushQueue(); } catch { /* ignore */ } }, 3000);
  } catch { /* ignore */ }
}

const sentCounts = new Map<string, number>();
let totalSent = 0;

// Statuses that are expected/controlled flows and must NOT be reported:
// 401/403 handled auth/plan flows, 413 FILE_TOO_LARGE (normal UX).
// 429 added 2026-09-30 (Phase-1 429 incident): the reporter is exempt from the
// global limiter, so a rate-limited session was converting every 429 into one
// more POST /api/client-errors (measured: 25 during the 19:40-20:01 window) —
// noise amplification, not signal; the limiter state is visible server-side.
const EXPECTED_STATUSES = new Set([401, 403, 413, 429]);

function isExpectedError(err: any): boolean {
  const status = Number(err?.response?.status || err?.status || 0);
  if (EXPECTED_STATUSES.has(status)) return true;
  const code = String(err?.response?.data?.code || err?.code || '');
  if (code === 'FILE_TOO_LARGE') return true;
  // 409 PLAN_STALE (2026-10-05, ticket #338): expected optimistic-concurrency
  // guard (2026-09-18, Jaime). The save UI already shows the server's reload
  // message and refetches fresh plan state (PricingConfigPage save handler);
  // reporting it at error level only auto-tickets noise via the journal
  // scanner (complaints #300, #338 — same fingerprint, post-incident window).
  // The 409 body carries error:'PLAN_STALE' (admin-routes.js PUT /plans/:id),
  // not data.code, so match that field explicitly; other 409s stay reported.
  const conflictCode = String(err?.response?.data?.error || err?.response?.data?.code || '');
  if (Number(err?.response?.status) === 409 && conflictCode === 'PLAN_STALE') return true;
  return false;
}

/** Recursive sanitizer: strips tokens/credentials, caps depth and size. */
function sanitize(value: any, depth = 0): any {
  try {
    if (value == null) return value;
    if (depth > 3) return '[depth-limit]';
    if (typeof value === 'string') {
      // Hardened PII scrub (Task 2): email/phone never ride in payloads;
      // boundary-shaped patterns keep UUIDs intact.
      return scrubText(value.length > 300 ? value.slice(0, 300) + '…' : value);
    }
    if (typeof value !== 'object') return value;
    if (Array.isArray(value)) return value.slice(0, 5).map((v) => sanitize(v, depth + 1));
    const out: Record<string, any> = {};
    for (const k of Object.keys(value).slice(0, 15)) {
      // Hardened key-drop (Task 2): email/name/phone-ish keys are removed,
      // not just credential keys.
      if (SENSITIVE_KEY_RE.test(k)) continue; // hard key-drop: name never appears at all
      out[k] = sanitize(value[k], depth + 1);
    }
    return out;
  } catch {
    return '[unserializable]';
  }
}

export interface ClientErrorReport {
  message: string;
  source?: string;
  level?: 'error' | 'warning' | 'info';
  context?: Record<string, any>;
}

/**
 * Fire-and-forget error report. Safe to call anywhere, anytime.
 * Never awaits, never throws, never shows UI.
 */
export function reportClientError(report: ClientErrorReport): void {
  try {
    const rawMessage = String(report.message || 'unknown error');
    const message = scrubText(rawMessage.slice(0, MAX_MESSAGE_LEN));

    // Dedupe signature: message head + level (source ignored so the same error
    // across routes still dedupes; context keeps it human-readable server-side).
    const signature = `${report.level || 'error'}::${message}`;
    const count = sentCounts.get(signature) || 0;
    if (count >= MAX_PER_SIGNATURE) return;
    if (totalSent >= MAX_TOTAL) return;

    // Guard against feedback loops: never report failures of this reporter.
    if (rawMessage.includes('client-errors') || rawMessage.includes('CLIENT_ERROR_LOOP')) return;

    const context = sanitize({
      ...(report.context || {}),
      ua: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    });

    const body = JSON.stringify({
      message,
      source: report.source || (typeof window !== 'undefined' ? window.location.pathname : '/'),
      level: report.level || 'error',
      context,
    }).slice(0, MAX_BODY_LEN);

    sentCounts.set(signature, count + 1);
    totalSent++;

    // CLIENT_ERROR: fire-and-forget POST, no auth headers, no awaiting.
    deliver(body);
  } catch {
    /* swallow everything */
  }
}

/** Convenience wrapper for axios/fetch failures (skips expected flows). */
export function reportApiFailure(err: any, context?: Record<string, any>) {
  try {
    if (isExpectedError(err)) return;
    const status = Number(err?.response?.status || 0);
    const msg =
      (typeof err?.response?.data?.error === 'string' && err.response.data.error) ||
      (typeof err?.message === 'string' && err.message) ||
      'API request failed';
    reportClientError({
      message: status ? `${status} ${msg}` : msg,
      level: 'error',
      context: {
        ...context,
        traceTail: selTraceTail(),
        status: status || undefined,
        url: String(err?.config?.url || err?.url || ''),
        method: String(err?.config?.method || '').toUpperCase() || undefined,
      },
    });
  } catch {
    /* swallow */
  }
}

/** Install global window.onerror / onunhandledrejection reporters. */
export function installGlobalErrorReporters(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (event) => {
    try {
      reportClientError({
        message: event.message || 'window.onerror',
        level: 'error',
        context: { file: event.filename, line: event.lineno, col: event.colno },
      });
    } catch {
      /* ignore */
    }
  });
  window.addEventListener('unhandledrejection', (event) => {
    try {
      const reason: any = event.reason;
      if (isExpectedError(reason)) return;
      reportClientError({
        message: typeof reason === 'string' ? reason : String(reason?.message || reason || 'unhandledrejection'),
        level: 'error',
        context: { stack: typeof reason?.stack === 'string' ? reason.stack.slice(0, 600) : undefined },
      });
    } catch {
      /* ignore */
    }
  });
}
